import { CloudArrowDownIcon, UserIcon } from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';
import { isEmpty } from 'radash';

import { Icon } from '@/components/core/Icon';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useTranslation } from '@/hooks/useTranslation';
import { createAssetLink } from '@/services/utils';

import ItemHistoryGrid, { AttributeData } from './ItemHistoryGrid';
import { extractAllDynamicKeys } from './utils';

const ItemHistoryTimeline = ({ data }: any ) => {
    const { t } = useTranslation();
    const { formatDate } = useDateFormatter();

    const columns: ColumnDef<any>[] = [
        {
            accessorKey: 'date',
            header: t('HISTORY.MODIFIED_WHEN'),
            cell: (info) => {
                const value = info.getValue();
                return value !== '' && value != null
                    ? formatDate(value as number, 'historyDateTime')
                    : '';
            },
            sortingFn: (rowA:any, rowB:any, columnId) => {
                const valueA = rowA.getValue(columnId);
                const valueB = rowB.getValue(columnId);
                if (!valueA && !valueB) return 0;
                if (!valueA) return 1;
                if (!valueB) return -1;
                return new Date(valueA).getTime() - new Date(valueB).getTime();
            },
            enableResizing: true,
        },{
            accessorKey: 'user',
            header: t('HISTORY.MODIFIED_BY'),
            enableResizing: true,
            cell: ({ row }) => (
                <span className='tw-flex tw-items-center tw-justify-center'>
                    <Icon icon={UserIcon} size='sm' variant='secondary'/>
                    <span className='tw-pl-1'> {row.getValue('user')}</span>
                </span>
            ),
        },{
            accessorKey: 'event',
            header: t('HISTORY.EVENT'),
            enableResizing: true,
        },{
            accessorKey: 'additionalText',
            header: t('HISTORY.ADDITIONAL_TEXT'),
            enableResizing: true,
        },
        {
            accessorKey: 'assetPath',
            header: t('HISTORY.LINK'),
            meta: {
                pinned: true
            },
            enableResizing: false,
            cell: ({ row }) => {
                const link: string = createAssetLink(row.original) || '';
                return (
                    <>
                        {!isEmpty(link) && (
                            <a target="_self" href={link} title={t('DOWNLOAD_LABEL')}>
                                <Icon icon={CloudArrowDownIcon} variant="default" size={'md'} />
                            </a>
                        )}
                    </>
                );
            },
        }
    ];

    const TimelineAttributesData = (itemsArray: AttributeData[]) => {
        return itemsArray.flatMap((item: any) => {
            const baseData = {
                date: item.updatedAt__ ?? '',
                user: item.updatedBy__ ? t(item.updatedBy__): '',
            };

            const dynamicKeys = extractAllDynamicKeys(item);

            if (dynamicKeys.length === 0) {
                return [baseData];
            }

            return dynamicKeys.map((key) => ({
                ...baseData,
                event: t('ATTRIBUTE_CHANGE'),
                additionalText: t('HISTORY.ATTRIBUTE_CHANGED_TEXT', {
                    attribute: key,
                    value: item[key]?.currentValue__ || ''
                }),
            }));
        });
    };

    const historyData = data?.attributeData?.history ? TimelineAttributesData(data.attributeData.history) : [];
    const eventData = data.eventData || [];
    const combinedData = [...historyData, ...eventData];

    return <ItemHistoryGrid data={combinedData} columns={columns} gridKey="Items-history-timeline" />;
};

export default ItemHistoryTimeline;
